import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useMemo } from "react";

import { getSchemaHookRegistry } from "./schemaRegistry";

import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import { useSortSearchParams, useTableSortHandler } from "@/lib/hooks/useSortSearchParams";
import type { SortDirection } from "@/types/table/tableProps";

export interface SchemaTableDataResult {
  tableData: unknown[];
  isLoading: boolean;
  isFetching: boolean;
  totalRecords: number;
  emptyMessage?: string;
  orderBy: string | null;
  descending: boolean;
  onSort: (accessor: string | undefined, direction: SortDirection | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
}

export function useSchemaTableData(
  integrationName: string | undefined,
  moduleName: string,
  clientId: string | undefined,
  tenantId: string | undefined
): SchemaTableDataResult | null {
  const { tenantId: authTenantId } = useAuth();
  const { page } = usePageSearchParams();
  const { pageSize } = usePageSizeSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();
  const handleSort = useTableSortHandler();

  const effectiveTenantId = tenantId ?? authTenantId ?? undefined;

  const moduleRegistry = getSchemaHookRegistry(moduleName);
  const registeredHook = moduleRegistry?.getTableDataHook?.(
    integrationName ?? "",
    clientId ?? undefined
  );

  const noOpQuery = useQuery<{ data: unknown[]; totalRecords: number }, AxiosError>({
    queryKey: ["no-op"],
    queryFn: () => Promise.resolve({ data: [], totalRecords: 0 }),
    enabled: false,
  });

  const queryResult: UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError> = registeredHook
    ? registeredHook({
      clientId,
      tenantId: effectiveTenantId,
      page,
      pageSize,
      orderBy,
      descending,
      search: debouncedSearchValue,
    })
    : noOpQuery;

  return useMemo(() => {
    if (!registeredHook) {
      return null;
    }

    const emptyMessage = (queryResult as unknown as { emptyMessage?: string }).emptyMessage;

    if (!queryResult.data) {
      return {
        tableData: [],
        isLoading: queryResult.isLoading,
        isFetching: queryResult.isFetching,
        totalRecords: 0,
        emptyMessage,
        orderBy: orderBy ?? null,
        descending: descending ?? false,
        onSort: handleSort as SchemaTableDataResult["onSort"],
        search: searchValue ?? "",
        onSearchChange: setSearchValue,
      };
    }

    const data = queryResult.data.data ?? [];
    const totalRecords = queryResult.data.totalRecords ?? 0;

    return {
      tableData: data,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
      totalRecords,
      emptyMessage,
      orderBy: orderBy ?? null,
      descending: descending ?? false,
      onSort: handleSort as SchemaTableDataResult["onSort"],
      search: searchValue ?? "",
      onSearchChange: setSearchValue,
    };
  }, [registeredHook, queryResult, orderBy, descending, handleSort, searchValue, setSearchValue]);
}
